import React, { useEffect, useState, useCallback, useRef } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { Link } from "react-router-dom";
import { Row, Col, Nav, Form } from "react-bootstrap";
import Avatar from "../../components/Avatar";
import { useAuth } from '../../utils/AuthContext';
import { getDataOnRealtimeDb, saveDataOnRealtimeDb, deleteDataOnRealtimeDb, updateDataOnRealtimeDb } from '../../utils/firebase';
import styles from './AIChat.module.css';
import { PremiumFeatureLock } from '../../components/premium/PremiumFeatureLock';

const TypingIndicator = () => (
    <div className={`${styles.message} ${styles.assistant}`}>
        <div className={styles.messageContent}>
            <div className={styles.messageHeader}>
                <i className="ri-robot-line"></i>
                <span>AI Assistant</span>
                <small>Typing...</small>
            </div>
            <div className={styles.typingIndicator}>
                <span></span>
                <span></span>
                <span></span>
            </div>
        </div>
    </div>
);

const Message = ({ message }) => {
    const getMessageClass = (role) => {
        switch (role) {
            case 'user':
                return 'user';
            case 'assistant':
                return 'assistant';
            case 'system':
                return 'system';
            default:
                return 'assistant';
        }
    };

    const getMessageIcon = (role) => {
        switch (role) {
            case 'user':
                return 'user';
            case 'assistant':
                return 'robot';
            case 'system':
                return 'information';
            default:
                return 'robot';
        }
    };

    const getMessageSender = (role) => {
        switch (role) {
            case 'user':
                return 'You';
            case 'assistant':
                return 'AI Assistant';
            case 'system':
                return 'System';
            default:
                return 'AI Assistant';
        }
    };

    return (
        <div className={`${styles.message} ${styles[getMessageClass(message.role)]}`}>
            <div className={styles.messageContent}>
                <div className={styles.messageHeader}>
                    <i className={`ri-${getMessageIcon(message.role)}-line`}></i>
                    <span>{getMessageSender(message.role)}</span>
                    <small>{new Date(message.timestamp).toLocaleTimeString()}</small>
                </div>
                <div 
                    dangerouslySetInnerHTML={{ 
                        __html: message.role === 'system' ? 
                            message.content : 
                            formatMessageContent(message.content) 
                    }}
                />
            </div>
        </div>
    );
};

const formatMessageContent = (text) => {
    if (!text) return '';
    try {
        return text
            .replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
            .replace(/`([^`]+)`/g, '<code>$1</code>')
            .replace(/^\s*[-*]\s+(.+)$/gm, '<li>$1</li>')
            .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
            .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>')
            .replace(/\n/g, '<br>');
    } catch (error) {
        console.error('Error formatting message:', error);
        return text || '';
    }
};

export default function AIChat() {
    const { currentUser, subscription } = useAuth();
    const [chatActive, setChatActive] = useState(null);
    const [msgShow, setMsgShow] = useState(false);
    const [chats, setChats] = useState([]);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const scrollRef = useRef(null);
    const isPremium = subscription === 'premium';

    const scrollToBottom = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    };

    const formatDate = (timestamp) => {
        try {
            const date = new Date(timestamp);
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            
            if (messageDate.getTime() === today.getTime()) {
                return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            } else if (today.getTime() - messageDate.getTime() <= 86400000) {
                return 'Yesterday';
            } else if (today.getTime() - messageDate.getTime() <= 6 * 86400000) {
                return date.toLocaleDateString([], { weekday: 'short' });
            } else {
                return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
            }
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    const loadChats = useCallback(async () => {
        try {
            const chatsRef = `users/${currentUser.uid}/conversations`;
            const chatsData = await getDataOnRealtimeDb(chatsRef);
            if (chatsData) {
                const chatList = Object.entries(chatsData)
                    .map(([id, chat]) => {
                        const lastMessage = chat.messages?.[chat.messages.length - 1];
                        const firstMessage = chat.messages?.[0];
                        const timestamp = chat.timestamp || new Date().toISOString();
                        
                        return {
                            id,
                            title: firstMessage ? firstMessage.content.substring(0, 30) + (firstMessage.content.length > 30 ? '...' : '') : 'New Chat',
                            timestamp,
                            lastMessage: lastMessage ? 
                                `${lastMessage.role === 'user' ? 'You: ' : 'AI: '}${lastMessage.content.substring(0, 30)}${lastMessage.content.length > 30 ? '...' : ''}` : 
                                'No messages yet',
                            lastUpdated: chat.lastUpdated || timestamp
                        };
                    })
                    .sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated));
                setChats(chatList);
            } else {
                setChats([]);
            }
        } catch (error) {
            console.error('Error loading chats:', error);
            setChats([]);
        }
    }, [currentUser]);

    const loadConversation = useCallback(async (chatId) => {
        try {
            const chatRef = `users/${currentUser.uid}/conversations/${chatId}`;
            const conversation = await getDataOnRealtimeDb(chatRef);
            if (conversation) {
                setMessages(conversation.messages || []);
                setChatActive(prev => ({
                    ...prev,
                    title: conversation.title || prev?.title
                }));
                setTimeout(scrollToBottom, 100);
            } else {
                setMessages([]);
            }
        } catch (error) {
            console.error('Error loading conversation:', error);
            setMessages([]);
        }
    }, [currentUser]);

    const createNewChat = useCallback(async () => {
        try {
            const now = new Date();
            const chatId = Date.now().toString();
            const newChat = {
                title: 'New Chat',
                timestamp: now.toISOString(),
                lastUpdated: now.toISOString(),
                messages: []
            };

            await saveDataOnRealtimeDb(`users/${currentUser.uid}/conversations/${chatId}`, newChat);
            
            const createdChat = { id: chatId, ...newChat };
            setChats(prev => [createdChat, ...prev]);
            setChatActive(createdChat);
            setMessages([]);
            setMsgShow(true);
        } catch (error) {
            console.error('Error creating chat:', error);
        }
    }, [currentUser]);

    const appendMessage = useCallback(async (chatId, message) => {
        try {
            // Get the specific chat reference
            const basePath = `users/${currentUser.uid}/conversations`;
            const currentChat = await getDataOnRealtimeDb(`${basePath}/${chatId}`);
            
            if (currentChat) {
                // Get existing messages and append new message
                const existingMessages = currentChat.messages || [];
                const updatedMessages = [...existingMessages, message];

                // Update database first
                await updateDataOnRealtimeDb(basePath, chatId, {
                    messages: updatedMessages,
                    lastUpdated: new Date().toISOString()
                });

                // Then update local state
                setMessages(updatedMessages);
                return updatedMessages;
            } else {
                // If chat doesn't exist, create new chat with first message
                const newChat = {
                    title: message.content.substring(0, 30) + (message.content.length > 30 ? '...' : ''),
                    timestamp: new Date().toISOString(),
                    lastUpdated: new Date().toISOString(),
                    messages: [message]
                };

                // Save new chat to database
                await saveDataOnRealtimeDb(`${basePath}/${chatId}`, newChat);
                
                // Update local state
                setMessages([message]);
                return [message];
            }
        } catch (error) {
            console.error('Error appending message:', error);
            return null;
        }
    }, [currentUser]);

    const handleSendMessage = useCallback(async (e) => {
        e.preventDefault();
        if (!inputMessage.trim() || !chatActive || isLoading) return;

        const userMessage = {
            role: 'user',
            content: inputMessage.trim(),
            timestamp: new Date().toISOString()
        };

        try {
            setIsLoading(true);
            const messageToSend = inputMessage.trim();
            setInputMessage('');

            // Update local state immediately for user feedback
            setMessages(prev => [...prev, userMessage]);
            scrollToBottom();

            // Save user message to database
            const updatedMessages = await appendMessage(chatActive.id, userMessage);
            if (!updatedMessages) {
                throw new Error('Failed to save user message');
            }

            // Send to AI service
            const response = await fetch(`${process.env.REACT_APP_GENERAL_PYTHON_APP}/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: messageToSend,
                    userId: currentUser.uid,
                    history: updatedMessages.map(msg => ({
                        role: msg.role,
                        content: msg.content
                    }))
                })
            });

            if (!response.ok) throw new Error('Network response was not ok');

            // Create initial AI message
            const assistantMessage = {
                role: 'assistant',
                content: '',
                timestamp: new Date().toISOString()
            };

            // Add empty AI message to show typing indicator
            const messagesWithAI = await appendMessage(chatActive.id, assistantMessage);
            if (!messagesWithAI) {
                throw new Error('Failed to save AI message');
            }

            // Process streaming response
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let fullResponse = '';

            while (true) {
                const { value, done } = await reader.read();
                
                if (done) {
                    // Update the final AI message
                    const basePath = `users/${currentUser.uid}/conversations`;
                    const currentChat = await getDataOnRealtimeDb(`${basePath}/${chatActive.id}`);
                    
                    if (currentChat && currentChat.messages) {
                        const finalMessage = {
                            ...assistantMessage,
                            content: fullResponse,
                            timestamp: assistantMessage.timestamp // Keep original timestamp for matching
                        };

                        const updatedMessages = currentChat.messages.map(msg =>
                            msg.timestamp === assistantMessage.timestamp ? finalMessage : msg
                        );

                        // Update database with final message
                        await updateDataOnRealtimeDb(basePath, chatActive.id, {
                            messages: updatedMessages,
                            lastUpdated: new Date().toISOString()
                        });

                        // Update local state
                        setMessages(updatedMessages);
                    }
                    scrollToBottom();
                    break;
                }

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n').filter(line => line.trim());

                for (const line of lines) {
                    if (line.trim() === 'data: [DONE]') continue;
                    if (line.startsWith('data: [ERROR]')) throw new Error(line.slice(12));
                    if (line.startsWith('data: ')) {
                        const content = line.slice(6);
                        fullResponse += content;
                        
                        // Update local state only during streaming
                        setMessages(prev => {
                            const updated = prev.map(msg => 
                                msg.timestamp === assistantMessage.timestamp ? {
                                    ...msg,
                                    content: fullResponse
                                } : msg
                            );
                            return updated;
                        });
                        
                        scrollToBottom();
                    }
                }
            }
        } catch (error) {
            console.error('Error sending message:', error);
            const errorMessage = {
                role: 'system',
                content: 'An error occurred while processing your message. Please try again.',
                timestamp: new Date().toISOString()
            };
            
            // Add error message to conversation
            await appendMessage(chatActive.id, errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [chatActive, currentUser, inputMessage, appendMessage, isLoading]);

    const deleteChat = useCallback(async (chatId) => {
        try {
            await deleteDataOnRealtimeDb(`users/${currentUser.uid}/conversations/${chatId}`);
            setChats(prev => prev.filter(chat => chat.id !== chatId));
            
            if (chatActive?.id === chatId) {
                setMessages([]);
                setChatActive(null);
                setMsgShow(false);
            }
        } catch (error) {
            console.error('Error deleting chat:', error);
        }
    }, [chatActive, currentUser]);

    const selectChat = useCallback(async (chat) => {
        setChatActive(chat);
        setMsgShow(true);
        await loadConversation(chat.id);
    }, [loadConversation]);

    useEffect(() => {
        loadChats();
    }, [loadChats]);


    if (!isPremium) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                        <h4 className="main-title mb-0">AI Chat</h4>
                    </div>

                    <PremiumFeatureLock 
                        title="AI-Powered Chat"
                        description="Get personalized financial advice and insights using our advanced AI algorithms. Upgrade to premium to access this feature."
                    />

                    <Footer />
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className={styles.chatContainer}>
                    <div className={styles.sidebar}>
                        <div className={styles.sidebarHeader}>
                            <h6 className="mb-0">Chat Messages</h6>
                            <Link onClick={createNewChat} title="New chat">
                                <i className="ri-chat-new-line"></i>
                            </Link>
                        </div>
                        <div className={styles.chatList}>
                            {chats.map((chat) => (
                                <div
                                    key={chat.id}
                                    className={`${styles.chatItem} ${chatActive?.id === chat.id ? styles.chatItemSelected : ''}`}
                                    onClick={() => selectChat(chat)}
                                >
                                    <div className={styles.chatItemHeader}>
                                        <h6 className="mb-0">{chat.title}</h6>
                                        <small>{formatDate(chat.timestamp)}</small>
                                    </div>
                                    <p className="mb-0 text-truncate">{chat.lastMessage}</p>
                                    <button
                                        className={styles.deleteButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteChat(chat.id);
                                        }}
                                    >
                                        <i className="ri-delete-bin-line"></i>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.mainChat}>
                        {!chatActive ? (
                            <div className={styles.emptyState}>
                                <i className={`ri-robot-line ${styles.emptyStateIcon}`}></i>
                                <h5>Welcome to AI Financial Assistant</h5>
                                <p className="text-muted mb-4">Start a new chat to get personalized financial advice</p>
                                <button className={styles.newChatButton} onClick={createNewChat}>
                                    <i className="ri-add-line"></i>
                                    Start New Chat
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className={styles.chatHeader}>
                                    <div className="d-flex align-items-center">
                                        <Avatar status="online" />
                                        <div className="ms-2">
                                            <h6 className="mb-0">AI Assistant</h6>
                                            <small className="text-muted">Always ready to help</small>
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.messageContainer} ref={scrollRef}>
                                    {messages.map((message, index) => (
                                        <Message key={`${message.timestamp}-${index}`} message={message} />
                                    ))}
                                    {isLoading && <TypingIndicator />}
                                </div>

                                <div className={styles.inputContainer}>
                                    <div className={styles.inputWrapper}>
                                        <input
                                            type="text"
                                            className={styles.input}
                                            placeholder="Write your message..."
                                            value={inputMessage}
                                            onChange={(e) => setInputMessage(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(e)}
                                            disabled={isLoading}
                                        />
                                        <button
                                            className={styles.sendButton}
                                            onClick={handleSendMessage}
                                            disabled={isLoading || !inputMessage.trim()}
                                        >
                                            <i className="ri-send-plane-2-line"></i>
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <Footer />
            </div>
        </React.Fragment>
    );
}